import React, { ReactNode, useEffect, useState } from 'react';
import { Collapse, SxProps } from '@mui/material';
import { Box } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useDashboardContext } from '../../context/DashboardContext';
import classnames from 'classnames';

interface DashboardItemProps {
  itemId: string | number;
  collapsedComponent: ReactNode;
  expandedComponent: ReactNode;
  sx?: SxProps;
  className?: string;
}

const DashboardItem: React.FC<DashboardItemProps> = ({
  itemId,
  collapsedComponent,
  expandedComponent,
  sx,
  className,
}) => {
  const { expandedItems, toggleExpand } = useDashboardContext();
  const [showCollapsed, setShowCollapsed] = useState(true);

  const isExpanded = expandedItems.has(itemId);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (isExpanded) {
      setShowCollapsed(false);
    } else {
      timer = setTimeout(() => {
        setShowCollapsed(true);
      }, 300);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isExpanded]);

  return (
    <Box
      className={classnames('dashboard-item-container', className)}
      sx={[
        {
          border: '1px solid',
          borderColor: 'border.default',
          borderRadius: '3px',
          padding: '2px 12px 2px 4px',
          marginBottom: '5px',
          display: 'flex',
          minHeight: '40px',
          justifyContent: 'space-between',
          transition: 'all 0.2s ease',
          alignItems: isExpanded ? 'flex-start' : 'center',
          '&:hover': {
            boxShadow: theme => `0 2px 4px ${theme.palette.common.softShadow}`,
            borderColor: 'primary.main',
          },
          '& .dashboard-item-content': { width: 'calc(100% - 27px)' },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <IconButton
        size="small"
        onClick={() => toggleExpand(itemId)}
        className={classnames('dashboard-item-toggle', { expanded: isExpanded })}
        sx={{
          marginRight: '4px',
          padding: 0,
          width: '23px',
          transition: 'transform 0.3s ease',
          transform: isExpanded ? 'rotate(180deg)' : 'none',
        }}
      >
        <KeyboardArrowDownIcon sx={{ width: '21px' }} />
      </IconButton>

      {showCollapsed && !isExpanded && <div className="dashboard-item-content">{collapsedComponent}</div>}

      <Collapse className="dashboard-item-content" in={isExpanded} timeout={300} unmountOnExit>
        <Box py="8px">{expandedComponent}</Box>
      </Collapse>
    </Box>
  );
};

export default DashboardItem;
